/**
 * shadcn/ui Table — 열이 많을 때 컨테이너에서 가로 스크롤.
 * @see https://ui.shadcn.com/docs/components/table
 */
import { cn } from '@/shared/lib/utils';
import * as React from 'react';

type TableProps = React.HTMLAttributes<HTMLTableElement> & {
  /** 스크롤 래퍼(div)에 추가 클래스 */
  containerClassName?: string;
};

const Table = React.forwardRef<HTMLTableElement, TableProps>(
  ({ className, containerClassName, ...props }, ref) => (
    <div
      data-slot="table-container"
      className={cn('relative w-full overflow-x-auto', containerClassName)}
    >
      <table
        ref={ref}
        className={cn(
          // min-w-max: 열을 압축하지 않고 콘텐츠 너비를 유지 → overflow 시 가로 스크롤
          'w-full min-w-max caption-bottom border-collapse text-sm',
          className,
        )}
        {...props}
      />
    </div>
  ),
);
Table.displayName = 'Table';

const TableHeader = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <thead
    ref={ref}
    className={cn(
      '[&_tr]:border-b [&_tr]:border-primary/15 [&_tr]:bg-primary/[0.06]',
      className,
    )}
    {...props}
  />
));
TableHeader.displayName = 'TableHeader';

const TableBody = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <tbody
    ref={ref}
    className={cn('[&_tr:last-child]:border-0', className)}
    {...props}
  />
));
TableBody.displayName = 'TableBody';

const TableFooter = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <tfoot
    ref={ref}
    className={cn('border-t border-border font-medium', className)}
    {...props}
  />
));
TableFooter.displayName = 'TableFooter';

const TableRow = React.forwardRef<
  HTMLTableRowElement,
  React.HTMLAttributes<HTMLTableRowElement>
>(({ className, ...props }, ref) => (
  <tr
    ref={ref}
    className={cn(
      'border-b border-border/60 transition-colors hover:bg-primary/[0.04] data-[state=selected]:bg-primary/10',
      className,
    )}
    {...props}
  />
));
TableRow.displayName = 'TableRow';

const tableCellDivider =
  '[&:not(:last-child)]:border-r [&:not(:last-child)]:border-primary/10';

const TableHead = React.forwardRef<
  HTMLTableCellElement,
  React.ThHTMLAttributes<HTMLTableCellElement>
>(({ className, ...props }, ref) => (
  <th
    ref={ref}
    className={cn(
      // RDS Checkbox(size-6) — 우측 pr-0이면 다음 열과 너무 붙음
      'h-10 whitespace-nowrap border-primary/10 px-4 text-left align-middle text-[0.7rem] font-semibold uppercase tracking-wider text-primary/90 [&:has([role=checkbox])]:pr-3',
      tableCellDivider,
      className,
    )}
    {...props}
  />
));
TableHead.displayName = 'TableHead';

const TableCell = React.forwardRef<
  HTMLTableCellElement,
  React.TdHTMLAttributes<HTMLTableCellElement>
>(({ className, ...props }, ref) => (
  <td
    ref={ref}
    className={cn(
      'whitespace-nowrap border-primary/10 px-4 py-3 align-middle [&:has([role=checkbox])]:pr-3',
      tableCellDivider,
      className,
    )}
    {...props}
  />
));
TableCell.displayName = 'TableCell';

export {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
};
